import { format, fromUnixTime } from 'date-fns';
import { FileWithPath } from 'react-dropzone/.';
import { fetchPlatform } from 'src/models/platform/platform';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import {
  populateHasManyRelationshipFromRequest,
  populateRelationshipFromRequest,
} from 'src/utils/populateRelationship';

import { Client } from '../client/_types';
import { normalize } from '../shared/normalizers';

import {
  AccountManager,
  Alert,
  ClientSearchResult,
  NotificationSetting,
  ProfileField,
  SessionUser,
  TwoFactorRequired,
  User,
  UserAuthorization,
  UserNutrition,
  WaiverFormData,
} from './_types';
import {
  denormalizeNotificationSetting,
  denormalizeUser,
  normalizeAccountManager,
  normalizeClientSearchResult,
  normalizeNotificationSetting,
  normalizeStartCCForClient,
  normalizeUser,
} from './normalizers';

interface FetchCurrentUserOptions {
  authToken?: string;
  withImpersonator?: boolean;
}

export const fetchCurrentUser = async ({
  authToken,
  withImpersonator = false,
}: FetchCurrentUserOptions): Promise<SessionUser | null> => {
  const response = await api.get(`/api/v4/users/me`, {
    params: {
      auth_token: authToken || '',
      with_impersonator: withImpersonator ? 'true' : undefined,
    },
  });
  if (response.data === null) {
    return null;
  }
  // Handle impersonation response structure
  if (response.data.user && response.data.impersonator) {
    const user = normalizeUser(response.data.user) as SessionUser;
    user.impersonator = normalizeUser(response.data.impersonator);
    user.impersonatedAt = response.data.impersonated_at;
    return user;
  }

  return normalizeUser(response.data) as SessionUser;
};

export const signInUser = async (
  email: string,
  password: string,
  rememberMe: boolean
): Promise<User | TwoFactorRequired> => {
  const response = await api.post(`/api/v4/users/sign_in`, {
    email: email,
    password: password,
    remember_me: rememberMe,
  });

  // Check if 2FA is required
  if (response.data.two_factor_required) {
    return {
      twoFactorRequired: true,
      userId: response.data.user_id,
      method: response.data.method,
      message: response.data.message,
    };
  }

  return normalizeUser(response.data);
};

export const signUpUser = async (
  email: string,
  password: string,
  name: string,
  optInMailing: boolean,
  timeZone: string
): Promise<User> => {
  const response = await api.post(`/api/v4/users/`, {
    user: {
      email: email,
      password: password,
      name: name,
      receive_newsletter: optInMailing,
      time_zone: timeZone,
    },
    sign_in: 'true',
  });
  return normalizeUser(response.data);
};

export const verifyTwoFactorCode = async (
  userId: number,
  otpCode: string
): Promise<User> => {
  const response = await api.post(`/api/v4/users/verify_otp`, {
    user_id: userId,
    otp_attempt: otpCode,
  });
  return normalizeUser(response.data);
};

export const resendTwoFactorCode = async (userId: number): Promise<void> => {
  await api.post(`/api/v4/users/resend_otp`, {
    user_id: userId,
  });
};

export const profileFieldsWithDefaults = (
  profileFields: ProfileField[],
  sessionTrainer?: User
): ProfileField[] => {
  // Create a copy of the profileFields to avoid mutating the original
  const updatedProfileFields = profileFields ? [...profileFields] : [];

  // Loop through trainerProfileFields and add any missing fields with empty string values
  if (
    sessionTrainer?.trainerProfileFields &&
    Array.isArray(sessionTrainer.trainerProfileFields)
  ) {
    sessionTrainer.trainerProfileFields.forEach((field) => {
      // Check if the field already exists in profileFields
      const existingField = updatedProfileFields.find(
        (pf) => pf.label === field
      );

      // If not, add it with an empty value
      if (!existingField) {
        updatedProfileFields.push({
          id: `profile_field_${Date.now()}`,
          label: field,
          value: '',
        });
      }
    });
  }

  return updatedProfileFields;
};

export const resetUserPassword = async (email: string): Promise<any> => {
  const response = await api.post(`/api/v4/users/password`, {
    email: email,
  });
  return response.data;
};

export const signOutUser = async (): Promise<void> => {
  await api.delete(`/api/v4/sign_out`);
};

interface FetchUserOptions {
  id: User['id'];
  withAlerts?: boolean;
  withDependents?: boolean;
}
export const fetchUser = async ({
  id,
  withAlerts,
  withDependents,
}: FetchUserOptions): Promise<User> => {
  const response = await api.get(`/api/v4/users/${id}`);
  const user = normalizeUser(response.data);
  if (withAlerts) {
    const alerts = await fetchUserAlerts(id);
    user.alerts = alerts;
  }
  if (withDependents) {
    const result = await fetchUserDependents({ id });
    user.dependents = result.dependents;
  }
  return user;
};

export const fetchUserDependents = async (options: {
  id: User['id'];
  search?: string;
  page?: number;
  perPage?: number;
}): Promise<{
  dependents: AccountManager[];
  total: number;
}> => {

  const response = await api.get(`/api/v3/account_managers`, {
    params: {
      id: options.id,
      managed: true,
      page: options.page || 1,
      per: options.perPage || 25,
      ...(options?.search && {
        q: {
            dependent_email_or_dependent_formatted_name_cont: options.search.trim(),
          }
      }),
    },
  });
  if (response.data) {
    return {
      dependents: response.data.account_manager.map((am) =>
        normalizeAccountManager(am)
      ),
      total: response.data.meta.total,
    };
  }
  return { dependents: [], total: 0 };
};

export const fetchUserManagers = async (
  id: User['id']
): Promise<AccountManager[]> => {
  const response = await api.get(`/api/v3/account_managers`, {
    params: {
      id: id,
      fetch_all: true,
    },
  });

  if (response.data) {
    return response.data.map((am) => normalizeAccountManager(am));
  }

  return [];
};

export const fetchUserAlerts = async (id: User['id']): Promise<Alert[]> => {
  const response = await api.get(`/api/v4/users/alerts_for_ids`, {
    params: {
      user_ids: id,
    },
  });
  return (response.data.alerts[0] && response.data.alerts[0].alerts) || [];
};

interface SearchUsersOptions {
  search: string;
  types?: string[];
  searchBy?: 'email' | 'phone_number' | 'name';
  noLimit?: boolean;
  trainersOnly?: boolean;
}

export const searchClients = async ({
  search,
  types = ['clients'],
  noLimit = false,
  trainersOnly = false,
}: SearchUsersOptions): Promise<ClientSearchResult[]> => {
  const response = await api.get('/sm/search', {
    params: {
      types,
      limit: 20,
      no_limit: noLimit,
      term: search,
      page: 1,
      trainers_only: trainersOnly,
    },
  });
  return response.data.results[types[0]].map((r) =>
    normalizeClientSearchResult(r)
  );
};

export const searchUserClients = async ({
  search,
  types = ['user'],
  searchBy,
}: SearchUsersOptions): Promise<ClientSearchResult[]> => {
  const response = await api.get('/sm/kiosk_search', {
    params: {
      types,
      limit: 20,
      no_limit: false,
      term: search,
      page: 1,
      search_by: searchBy,
    },
  });

  return response.data.results[types[0]].map((r) =>
    normalizeClientSearchResult(r)
  );
};

const attachDependentsToUser = async (users: User[]): Promise<User[]> => {
  return await populateHasManyRelationshipFromRequest({
    list: users,
    idKey: 'id',
    newKey: 'dependents',
    foreignKey: 'managerId',
    func: (ids) => FetchUsersDependents({ ids }),
  });
};

const attachAlertsToUsers = async (users: User[]): Promise<User[]> => {
  const usersWithAlerts = await populateRelationshipFromRequest({
    list: users,
    idKey: 'id',
    newKey: 'alerts',
    func: async (ids: User['id'][]) => {
      const alerts: any[] = await fetchUsersAlerts({ ids });
      return alerts.map((a) => ({
        id: a.user_id,
        alerts: a.alerts,
      }));
    },
  });

  return usersWithAlerts.map((user) => {
    user.alerts = user.alerts?.alerts || [];
    return user;
  });
};

interface FetchAdminUsersOptions {
  page?: number;
  perPage?: number;
  search?: string;
  withPlatform?: boolean;
  filters?: {
    role?: string;
    trainerId?: string;
    deleted?: boolean;
  };
  sortBy?: string;
  sortDir?: string;
  emailOnly?: boolean;
}

interface FetchUserResponse {
  users: User[];
  total: number;
}

export const fetchAdminUsers = async ({
  page,
  perPage,
  search,
  withPlatform = true,
  filters,
  sortBy,
  sortDir,
  emailOnly,
}: FetchAdminUsersOptions): Promise<FetchUserResponse> => {
  const params = {
    q: {
      s: sortBy && sortDir ? `${sortBy} ${sortDir}` : null,
      has_trainer: filters?.trainerId || null,
      deleted_at_not_null: filters?.deleted ? 1 : null,
      ...(filters?.role && { user_role: filters.role }),
    },
    page: page || 1,
    per: perPage || 10,
  } as any;

  if (search) {
    if (emailOnly) {
      params.q.email_eq = search.toLowerCase().trim();
    } else {
      params.q.groupings = [
        {
          formatted_name_cont: search,
          email_eq: search,
          m: 'or',
        },
      ];
    }
  }

  const response = await api.get(`/api/v4/users`, {
    params: params,
  });

  // The response structure is { user: [], meta: { total: number } }
  let users = response.data.user
    ? response.data.user.map((user) => normalizeUser(user))
    : [];
  if (withPlatform) {
    users = await populateRelationshipFromRequest({
      list: users,
      idKey: 'platformId',
      newKey: 'platform',
      func: (ids) => Promise.all(ids.map((id) => fetchPlatform({ id }))),
    });
  }

  return { users, total: response.data.meta.total };
};

interface FetchUsersOptions {
  ids?: User['id'][];
  search?: string;
  withAlerts?: boolean;
  excludeStaff?: boolean;
  fetchTrainers?: boolean;
  withDependents?: boolean;
  clientId?: Client['id'];
  fetchAll?: boolean;
}

export const fetchUserQueryKey = (o: FetchUsersOptions) => [
  'user',
  o.ids,
  o.search,
  o.clientId,
  o.excludeStaff,
  o.fetchTrainers,
  o.withDependents,
  o.withAlerts,
  o.fetchAll,
];

export const fetchUsers = async ({
  withAlerts,
  ids,
  search,
  clientId,
  excludeStaff,
  fetchTrainers,
  withDependents,
  fetchAll = true,
}: FetchUsersOptions): Promise<User[]> => {
  const params = {
    with_ids: ids?.join(','),
    exclude_staff: !!excludeStaff,
    ...(fetchAll ? { fetch_all: true } : { page: 1, per: 30 }),
    q: { s: 'first_name asc' },
    trainers: fetchTrainers ? true : null,
    'q[barcode_hash_or_formatted_name_or_email_cont]': search,
    'q[trainer_association_id_eq]': clientId,
  };

  const response = await api.get(`/api/v4/users`, {
    params: params,
  });
  const users = response.data?.user
    ? response.data.user.map((user) => normalizeUser(user))
    : response.data.map((user) => normalizeUser(user));

  if (withAlerts && withDependents) {
    const result1 = await attachDependentsToUser(users);
    const result = attachAlertsToUsers(result1);

    return result;
  } else if (withAlerts) {
    return attachAlertsToUsers(users);
  } else if (withDependents) {
    return attachDependentsToUser(users);
  } else {
    return users;
  }
};

interface FetchUsersAlertsOptions {
  ids?: User['id'][];
}

export const fetchUsersAlerts = async ({
  ids,
}: FetchUsersAlertsOptions): Promise<Alert[]> => {
  const response = await api.get(`/api/v4/users/alerts_for_ids`, {
    params: {
      user_ids: ids?.join(','),
    },
  });
  return response.data.alerts;
};

interface FetchUsersDependentsOptions {
  ids?: User['id'][];
}

export const FetchUsersDependents = async ({
  ids,
}: FetchUsersDependentsOptions): Promise<AccountManager[]> => {
  const response = await api.get(`/api/v3/account_managers`, {
    params: {
      manager_ids: ids?.join(','),
      managed: true,
    },
  });
  if (response.data) {
    return response.data.account_manager.map((am) =>
      normalizeAccountManager(am)
    );
  }
  return [];
};

export const updateUser = async (
  userId: User['id'],
  user: Partial<User>
): Promise<User> => {
  const data = denormalizeUser(user);

  const response = await api.put(`/api/v4/users/${userId}`, { user: data });
  return normalizeUser(response.data);
};

export const uploadUserProfileImage = async (
  userId: User['id'],
  file: File,
  fieldName?: string
): Promise<User> => {
  const response = await api.put(
    `/api/v4/users/${userId}`,
    {
      user: {
        [fieldName || 'profile_image']: file,
      },
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return normalizeUser(response.data);
};

interface FetchUserNutritionOptions {
  userId: User['id'];
  date: number; // unix timestamp
}

export const fetchUserNutrition = async ({
  userId,
  date,
}: FetchUserNutritionOptions): Promise<UserNutrition> => {
  const response = await api.get(`/api/v2/users/nutrition/`, {
    params: {
      user_id: userId,
      date,
      date_string: format(fromUnixTime(date), 'yyyy-MM-dd'),
    },
  });

  const data = camelcaseKeys(response.data, { deep: true });

  return data;
};

interface createDependentPayload {
  dependent: Partial<User>;
  accountManager: {
    id: Identifier | undefined;
    managerId: Identifier | undefined;
    relationship: string;
    useCard?: boolean;
    waiver?: string;
    assessments?: string;
    emails?: string;
  };
  withLogin: boolean;
}

interface CreateAccountManagerPayload {
  dependentId: Identifier;
  managerId: Identifier;
  relationship: string;
  useCard?: boolean;
  waiver?: string;
  assessments?: string;
  emails?: string;
}

export const createAccountManager = async (
  payload: CreateAccountManagerPayload
) => {
  const response = await api.post('/api/v3/account_managers', {
    account_manager: {
      dependent_id: payload.dependentId,
      manager_id: payload.managerId,
      relationship: payload.relationship,
      use_card: payload.useCard ?? false,
      waiver: payload.waiver,
      assessments: payload.assessments,
      emails: payload.emails,
    },
  });

  return response.data;
};

export const fetchGAdmins = async (with_ids): Promise<any[]> => {
  const response = await api.get(`/api/v2/users?with_ids=${with_ids}`);
  return response.data.map(normalize);
};
export const createDependent = async ({
  dependent,
  accountManager,
  withLogin,
}: createDependentPayload): Promise<{ user: User; accountManager: any }> => {
  try {
    const response = await api.post('/api/v2/users', {
      user: denormalizeUser(dependent),
      dependent: true,
      no_password: !withLogin,
    });

    const user = normalizeUser(response.data);

    const relationshipResponse = await createAccountManager({
      dependentId: user.id,
      managerId: accountManager.managerId!,
      relationship: accountManager.relationship,
      useCard: accountManager.useCard,
      waiver: accountManager.waiver,
      assessments: accountManager.assessments,
      emails: accountManager.emails,
    });

    return { user, accountManager: relationshipResponse };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error assigning assessment:', error);

    throw error;
  }
};

export const fetchTrainers = async (): Promise<User[]> => {
  const response = await api.get(`/api/v2/users`, {
    params: {
      trainers: true,
      fetch_all: true,
      q: { s: 'first_name asc' },
      simple: true,
    },
  });
  return response.data.map(normalize);
};

export const fetchUserAuthorizations = async (): Promise<
  UserAuthorization[]
> => {
  const response = await api.get(`/api/v2/users/partners`);
  return response.data.map(normalize);
};

export const fetchTrainerEmails = async (): Promise<
  {
    name: string;
    value: string;
  }[]
> => {
  const response = await api.get(`/api/v2/users/trainer_emails`, {
    params: {
      web_settings: true,
    },
  });
  return response.data.map(normalize);
};

interface BulkUpdateUsersParams {
  userIds: User['id'][];
  disableEmails?: boolean;
  selectAll?: boolean;
  filters?: {
    trainerId?: string;
    status?: string;
    group?: string;
    workoutPlan?: string;
    tags?: string[];
    matchAllTags?: boolean;
    customStatus?: string;
    lifecycleStage?: number;
  };
}

export const bulkUpdateUsers = async ({
  userIds,
  disableEmails,
  selectAll,
  filters,
}: BulkUpdateUsersParams): Promise<void> => {
  await api.post('/api/v3/users/bulk_update', {
    user_ids: userIds,
    ...(disableEmails !== undefined && {
      disable_all_account_emails: disableEmails,
    }),
    ...(selectAll && {
      all_pages: true,
      // Include current filters when selecting all pages
      ...(filters && {
        'q[has_trainer]': filters.trainerId,
        'q[has_status]': filters.status,
        'q[client_filter_group]': filters.group,
        'q[filter_plan]': filters.workoutPlan,
        'q[has_lifecycle_stage]': filters.lifecycleStage,
        'q[client_filter]': filters.customStatus,
        ...(!filters.matchAllTags &&
          filters.tags && { 'q[tags_name_eq_any]': filters.tags }),
        ...(filters.matchAllTags &&
          filters.tags && { 'q[client_tags]': filters.tags.join(',') }),
      }),
    }),
  });
};

export const fetchUserById = async (userId: User['id']): Promise<User> => {
  const response = await api.get(`/api/v3/users/${userId}`);
  return normalizeUser(response.data);
};

export const deleteUser = async (userId: User['id']): Promise<void> => {
  await api.delete(`/api/v3/users/${userId}`);
};

export const mergeAccounts = async ({
  userId,
  newUserId,
}: {
  userId: User['id'];
  newUserId: User['id'];
}): Promise<void> => {
  await api.post(`/api/v4/users/${userId}/copy_users/`, {
    new_user_id: newUserId,
  });
};

export const updateDependent = async ({
  dependent,
  accountManager,
  withLogin,
}: createDependentPayload): Promise<{ user: User; accountManager: any }> => {
  try {
    const response = await api.put(`/api/v3/users/${dependent.id}`, {
      user: denormalizeUser(dependent),
      dependent: true,
      no_password: !withLogin,
    });

    const user = normalizeUser(response.data);

    const relationshipResponse = await api.put(
      `/api/v3/account_managers/${accountManager.id}`,
      {
        account_manager: {
          dependent_id: user.id,
          relationship: accountManager.relationship,
          use_card: accountManager.useCard ?? false,
          waiver: accountManager.waiver,
          assessments: accountManager.assessments,
          emails: accountManager.emails,
        },
      }
    );

    return { user, accountManager: relationshipResponse.data };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error updating dependent:', error);

    throw error;
  }
};

export const deleteAccountManager = async (id: Identifier): Promise<void> => {
  await api.delete(`/api/v3/account_managers/${id}`);
};

export const submitBusinessWaiver = async (
  Waiverdata: WaiverFormData
): Promise<{ subdomain: string; redirectToken: string }> => {
  const data = {
    user: {
      first_name: Waiverdata.firstName,
      last_name: Waiverdata.lastName,
      time_zone: Waiverdata.timeZone,
      street_address: Waiverdata.streetAddress,
      city: Waiverdata.city,
      state: Waiverdata.state,
      zip_code: Waiverdata.zipCode,
      brand: Waiverdata.brand,
      password: Waiverdata.password,
      password_confirmation: Waiverdata.confirmPassword,
      country_address: Waiverdata.countryAddress,
    },
    accepted_waiver: Waiverdata.acceptedWaiver,
    subdomain: Waiverdata.subdomain,
    import_exercises: Waiverdata.importExercises,
    signature: Waiverdata.signature,
    currency: Waiverdata.currency,
  };
  const response = await api.post(`/api/v4/users/update_business_waiver`, data);
  return response.data;
};

export interface ServiceBalanceSummary {
  balance: number;
  afterVisits: number;
  hasBalance: boolean;
}

export const fetchServiceBalanceSummary = async (
  userId?: User['id']
): Promise<ServiceBalanceSummary> => {
  const response = await api.get(
    '/api/v4/fbm/service_balance_transactions/balance_summary',
    {
      params: {
        user_id: userId,
      },
    }
  );

  return camelcaseKeys(response.data);
};

interface RequiredItemsResponse {
  redirect?: string;
}

export const checkRequiredItems = async (): Promise<RequiredItemsResponse> => {
  const response = await api.get('/api/v4/users/required_items');
  return response.data;
};

/**
 * Update trainer measurementsOrder field
 */
export const updateTrainerMeasurementsOrder = async (
  trainerId: string,
  measurementsOrder: string[]
): Promise<any> => {
  return updateUser(trainerId, { measurementsOrder } as any);
};

export const mindbodyImport = async (file: FileWithPath): Promise<any> => {
  const fd = new FormData();
  fd.append('spreadsheet[file]', file);
  const response = await api.post('/api/v3/users/mindbody_import', fd);
  return response.data;
};

export const fetchNotificationSettings = async ({
  webSettings = false,
}: {
  webSettings?: boolean;
}): Promise<NotificationSetting[]> => {
  const response = await api.get('/api/v2/users/settings', {
    params: {
      web_settings: webSettings,
    },
  });
  return response.data.map(normalizeNotificationSetting);
};

export const updateNotificationSettings = async (
  settings: NotificationSetting[]
): Promise<{ success: number }> => {
  const data = settings.map(denormalizeNotificationSetting);
  const response = await api.post(
    '/api/v2/users/update_settings',
    {
      web_settings: true,
      settings: data,
    },
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
    }
  );
  return response.data;
};

export const startUpdateCC = async ({ forClient }: { forClient: boolean }) => {
  const response = await api.get('/api/v4/users/start_update_cc/', {
    params: {
      for_client: forClient,
    },
  });
  return normalizeStartCCForClient(response.data);
};
